"use client";

import { useEffect, useRef, useState, useEffectEvent } from "react";
import { usePathname, useSearchParams } from "next/navigation";

import type { Block, Document, Inline, Text } from "@contentful/rich-text-types";
import LeadGenFormModal from "@/app/components/studio/LeadGenFormModal/LeadGenFormModal";
import type { TFormData } from "@/app/components/studio/Quiz/Quiz.helpers";
import { LOCAL_STORAGE_KEYS } from "@/lib/constants";
import type { ExperimentalModalEntry } from "@/lib/services/contentful/getExperimentalModal";
type ExperimentModalClientProps = {
	experimentalModal: ExperimentalModalEntry;
	navigateToHref?: (href: string) => void;
};

const PAGE_LOAD_TRIGGER = "on page load";
const PAGE_SWITCH_TRIGGER = "on page switch";
const LEAVING_SITE_TRIGGER = "on leaving site";

export default function ExperimentModalClient({
	experimentalModal,
	navigateToHref = (href) => globalThis.window.location.assign(href),
}: Readonly<ExperimentModalClientProps>) {
	const trigger = experimentalModal.trigger ?? undefined;
	const pathname = usePathname();
	const searchParams = useSearchParams().toString();

	const pendingExternalHref = useRef<string | null>(null);

	const [open, setOpen] = useState(false);

	const openModal = useEffectEvent(() => {
		if (hasExperimentalModalBeenOpened(experimentalModal)) {
			return;
		}

		saveExperimentalModalOpened(experimentalModal);
		setOpen(true);
	});

	useEffect(() => {
		if (
			trigger !== PAGE_LOAD_TRIGGER ||
			!getModalEligibility(experimentalModal, pathname, searchParams)
		) {
			setOpen(false);
			return;
		}

		const delaySeconds = experimentalModal.delay ?? 0;
		const timeoutId = setTimeout(() => {
			openModal();
		}, delaySeconds * 1000);

		return () => {
			clearTimeout(timeoutId);
		};
	}, [experimentalModal, pathname, searchParams, trigger]);

	useEffect(() => {
		if (trigger !== LEAVING_SITE_TRIGGER && trigger !== PAGE_SWITCH_TRIGGER) {
			return;
		}

		function handleDocumentClick(event: MouseEvent) {
			const link = (event.target as Element | null)?.closest("a[href]");
			if (!link) {
				return;
			}
			const isEligible = getModalEligibility(experimentalModal, pathname, searchParams);

			const showOnPageLeave =
				trigger === LEAVING_SITE_TRIGGER && isEligible && canShowModalOnExternalLink(link);
			const showOnPageSwitch =
				trigger === PAGE_SWITCH_TRIGGER && isEligible && canShowModalOnPageSwitch(link);

			if (showOnPageLeave || showOnPageSwitch) {
				event.preventDefault();
				pendingExternalHref.current = link.href;
				openModal();
			}
		}

		document.addEventListener("click", handleDocumentClick, true);

		return () => {
			document.removeEventListener("click", handleDocumentClick, true);
		};
	}, [experimentalModal, pathname, searchParams, trigger]);

	function handleClose() {
		setOpen(false);

		if (pendingExternalHref.current) {
			navigateToHref(pendingExternalHref.current);
			pendingExternalHref.current = null;
		}
	}

	if (!open) {
		return null;
	}

	return (
		<LeadGenFormModal
			open={open}
			onClose={handleClose}
			leadGenPopUpImageDesktop={experimentalModal.heroImage?.url ?? undefined}
			leadGenPopUpImageAlt={getHeroImageAlt(experimentalModal)}
			quizFormData={createExperimentalModalQuizFormData(
				experimentalModal,
				pendingExternalHref.current,
			)}
			submitEndpointUrl={getSubmitEndpointUrl(experimentalModal)}
		/>
	);
}

function getSubmitEndpointUrl(experimentalModal: ExperimentalModalEntry) {
	return experimentalModal.submitEndpointUrl || "";
}

function getHeroImageAlt(experimentalModal: ExperimentalModalEntry) {
	return experimentalModal.heroImageAlt || experimentalModal.heroImage?.description || undefined;
}

export function createExperimentalModalQuizFormData(
	content: ExperimentalModalEntry,
	pendingExternalHref: string | null = null,
): TFormData {
	const heading = content.headline ?? "Ready for a new smile?";
	const description = documentToPlainText(content.bodyText?.json as Document | undefined);

	return {
		form: {
			brand: "Aspen Dental",
			form_title: content.formTitle ?? heading,
			product_line: "Aspen Dental",
			form_description: `${heading} Lead Gen Quiz`,
			form_type: "Quiz",
			form_channel: "Web",
			session_id: "",
			form_questions: [],
			user_form: {
				title: heading,
				eyebrow: content.badgeLabel ?? "",
				subTitle: content.promoBoldText ?? "",
				description,
				descriptionRichText: content.bodyText ?? undefined,
				isSignatureEliteResult: false,
				fields: getEnabledFormFields(content),
				submitButtonLabel: content.primaryCtaLabel ?? "",
				closeButtonLabel: content.dismissLabel ?? "",
				disclaimer: content.optInConsentLine ?? undefined,
				legalMessage: content.legalDisclaimerFootnote ?? "",
				successTitleText: content.successTitleText ?? "Thank you for signing up!",
				persistSubmission: true,
				successRedirectUrl: pendingExternalHref ?? undefined,
			},
		},
	};
}

function getEnabledFormFields(
	content: ExperimentalModalEntry,
): TFormData["form"]["user_form"]["fields"] {
	const fields: TFormData["form"]["user_form"]["fields"] = [];

	if (content.includeFirstName) {
		fields.push({
			name: "firstName",
			label: "First name*",
			required: true,
			unique_label: "first_name",
			type: "Text",
			value: null,
		});
	}

	if (content.includeLastName) {
		fields.push({
			name: "lastName",
			label: "Last name*",
			required: true,
			unique_label: "last_name",
			type: "Text",
			value: null,
		});
	}

	if (content.includeEmail) {
		fields.push({
			name: "email",
			label: "Email*",
			required: true,
			unique_label: "email_address",
			type: "Text",
			value: null,
		});
	}

	return fields;
}

function documentToPlainText(document: Document | undefined) {
	return document?.content.map((node) => getNodeText(node)).join("\n") ?? "";
}

function getNodeText(node: Block | Inline | Text): string {
	if ("value" in node) {
		return String(node.value);
	}

	return node.content?.map((childNode) => getNodeText(childNode)).join("") ?? "";
}

function getModalEligibility(
	experimentalModal: ExperimentalModalEntry,
	pathname: string | null,
	searchParams: string,
) {
	return (
		matchesExperimentalModalTargeting(experimentalModal, pathname, searchParams) &&
		!hasExperimentalModalBeenOpened(experimentalModal) &&
		!isExperimentalModalAlreadySubmitted(experimentalModal)
	);
}

type TargetingPatterns = ReadonlyArray<string | null> | null | undefined;

export function matchesExperimentalModalTargeting(
	experimentalModal: Pick<ExperimentalModalEntry, "excludePaths" | "includePaths" | "queryParams">,
	pathname: string | null,
	searchParams: string,
) {
	const urlValues = getUrlValues(pathname, searchParams);

	return (
		matchesRequiredPatterns(experimentalModal.includePaths, urlValues) &&
		!matchesAnyPattern(experimentalModal.excludePaths, urlValues) &&
		matchesRequiredPatterns(experimentalModal.queryParams, getSearchParamValues(searchParams))
	);
}

function getUrlValues(pathname: string | null, searchParams: string) {
	const path = pathname ?? "";
	const relativeUrl = searchParams ? `${path}?${searchParams}` : path;
	const absoluteUrl = globalThis.window?.location?.href ?? null;

	return absoluteUrl ? [path, relativeUrl, absoluteUrl] : [path, relativeUrl];
}

function getSearchParamValues(searchParams: string) {
	return searchParams ? [searchParams, `?${searchParams}`] : [searchParams];
}

function matchesRequiredPatterns(patterns: TargetingPatterns, values: ReadonlyArray<string>) {
	return getConfiguredPatterns(patterns).length === 0 || matchesAnyPattern(patterns, values);
}

function matchesAnyPattern(patterns: TargetingPatterns, values: ReadonlyArray<string>) {
	return getConfiguredPatterns(patterns).some((pattern) =>
		values.some((value) => matchesPattern(value, pattern)),
	);
}

function getConfiguredPatterns(patterns: TargetingPatterns) {
	return (patterns ?? []).flatMap((pattern) => {
		const trimmedPattern = pattern?.trim();
		return trimmedPattern ? [trimmedPattern] : [];
	});
}

function matchesPattern(value: string, pattern: string) {
	return value.includes(pattern) || toRegularExpression(pattern)?.test(value) === true;
}

function toRegularExpression(pattern: string) {
	const match = /^\/(.+)\/([a-z]*)$/.exec(pattern);
	const isRegexLiteral =
		match !== null && (match[2].length > 0 || hasRegularExpressionSyntax(match[1]));
	const source = isRegexLiteral ? match[1] : pattern;
	const flags = isRegexLiteral ? match[2] : "";

	if (!hasRegularExpressionSyntax(source) && !flags) {
		return null;
	}

	try {
		return new RegExp(source, flags);
	} catch (error) {
		if (error instanceof SyntaxError) {
			return null;
		}

		throw error;
	}
}

function hasRegularExpressionSyntax(pattern: string) {
	return /[\\^$*+?()[\]{}|]/.test(pattern);
}

function isExperimentalModalAlreadySubmitted(experimentalModal: ExperimentalModalEntry) {
	if (globalThis.window === undefined) {
		return false;
	}

	const formData = createExperimentalModalQuizFormData(experimentalModal);

	return localStorage.getItem(getLeadGenSubmissionStorageKey(formData)) === "true";
}

function saveExperimentalModalOpened(experimentalModal: ExperimentalModalEntry) {
	if (globalThis.window === undefined) {
		return;
	}

	localStorage.setItem(getExperimentalModalOpenedStorageKey(experimentalModal), "true");
}

function hasExperimentalModalBeenOpened(experimentalModal: ExperimentalModalEntry) {
	if (globalThis.window === undefined) {
		return false;
	}

	return localStorage.getItem(getExperimentalModalOpenedStorageKey(experimentalModal)) === "true";
}

function getExperimentalModalOpenedStorageKey(experimentalModal: ExperimentalModalEntry) {
	const modalIdentifier =
		experimentalModal.modalId ??
		experimentalModal.sys.id ??
		createExperimentalModalQuizFormData(experimentalModal).form.form_title;

	return `EXPERIMENTAL_MODAL_OPENED_${modalIdentifier}`;
}

function getLeadGenSubmissionStorageKey(formData: TFormData) {
	return `${LOCAL_STORAGE_KEYS.LEAD_GEN_FORM_SUBMITTED}_${formData.form.form_title}`;
}

function canShowModalOnExternalLink(link: Element): link is HTMLAnchorElement {
	if (!(link instanceof HTMLAnchorElement)) {
		return false;
	}

	if (link.target && link.target !== "_self") {
		return false;
	}
	return link.origin !== globalThis.window.location.origin;
}

function canShowModalOnPageSwitch(link: Element): link is HTMLAnchorElement {
	if (!(link instanceof HTMLAnchorElement)) {
		return false;
	}

	if (link.target && link.target !== "_self") {
		return false;
	}
	return (
		link.origin === globalThis.window.location.origin &&
		link.pathname !== globalThis.window.location.pathname &&
		!link.pathname.includes("schedule-an-appointment")
	);
}
